import { createTheme } from '@mui/material/styles';

// PS 스타일 테마 정의
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00E5FF',
      light: '#4CB9E7',
      dark: '#1E5F8C',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7B68EE',
      light: '#9F8FEF',
      dark: '#5A4CBE',
      contrastText: '#ffffff',
    },
    background: {
      default: '#051336',
      paper: 'rgba(10, 36, 99, 0.8)',
      lighter: 'rgba(76, 185, 231, 0.1)',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    error: {
      main: '#FF5252',
    },
    warning: {
      main: '#FFC107',
    },
    info: {
      main: '#64B5F6',
    },
    success: {
      main: '#4CAF50',
    },
    divider: 'rgba(76, 185, 231, 0.3)',
    // 차트 색상 팔레트 추가
    chart: {
      c1: '#00E5FF',
      c2: '#7B68EE',
      c3: '#FF5252',
      c4: '#FFC107',
      c5: '#4CAF50',
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01562em',
      color: '#00E5FF',
      textShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
      letterSpacing: '-0.00833em',
      color: '#00E5FF',
      textShadow: '0 0 8px rgba(0, 229, 255, 0.5)',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.2,
      letterSpacing: '0em',
      color: '#4CB9E7',
      textShadow: '0 0 6px rgba(76, 185, 231, 0.5)',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.2,
      letterSpacing: '0.00735em',
      color: '#4CB9E7',
      textShadow: '0 0 5px rgba(76, 185, 231, 0.5)',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.2,
      letterSpacing: '0em',
      color: '#4CB9E7',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.2,
      letterSpacing: '0.0075em',
      color: '#4CB9E7',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
      color: 'rgba(255, 255, 255, 0.9)',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      letterSpacing: '0.00714em',
      color: 'rgba(255, 255, 255, 0.9)',
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      letterSpacing: '0.01071em',
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
    caption: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    overline: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#051336',
          backgroundImage: 'radial-gradient(circle at 50% 50%, #0A2463 0%, #051336 100%)',
          backgroundAttachment: 'fixed',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(10, 36, 99, 0.2)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(76, 185, 231, 0.5)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0, 229, 255, 0.7)',
            },
          },
        },
      },
    },
    // MuiPaper-root 클래스 PS 스타일 오버라이드
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 36, 99, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 12,
          boxShadow: '0 0 20px rgba(0, 229, 255, 0.2), inset 0 0 20px rgba(0, 229, 255, 0.05)',
          border: '1px solid rgba(76, 185, 231, 0.3)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 0 25px rgba(0, 229, 255, 0.3), inset 0 0 25px rgba(0, 229, 255, 0.1)',
          },
        },
        elevation1: {
          boxShadow: '0 0 20px rgba(0, 229, 255, 0.2), inset 0 0 20px rgba(0, 229, 255, 0.05)',
        },
        elevation2: {
          boxShadow: '0 0 25px rgba(0, 229, 255, 0.25), inset 0 0 25px rgba(0, 229, 255, 0.075)',
        },
        elevation3: {
          boxShadow: '0 0 30px rgba(0, 229, 255, 0.3), inset 0 0 30px rgba(0, 229, 255, 0.1)',
        },
        elevation4: {
          boxShadow: '0 0 35px rgba(0, 229, 255, 0.35), inset 0 0 35px rgba(0, 229, 255, 0.125)',
        },
        elevation5: {
          boxShadow: '0 0 40px rgba(0, 229, 255, 0.4), inset 0 0 40px rgba(0, 229, 255, 0.15)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 36, 99, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 0, // 모서리를 직각으로 변경 (0으로 설정)
          overflow: 'hidden',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderBottom: '1px solid rgba(76, 185, 231, 0.2)',
        },
        title: {
          color: '#00E5FF',
          fontWeight: 600,
          textShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
        },
        subheader: {
          color: 'rgba(255, 255, 255, 0.7)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: '1px solid rgba(76, 185, 231, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'all 0.3s ease',
        },
        contained: {
          background: 'linear-gradient(45deg, #1E5F8C 0%, #4CB9E7 100%)',
          boxShadow: '0 4px 10px rgba(0, 229, 255, 0.3)',
          '&:hover': {
            background: 'linear-gradient(45deg, #1E5F8C 0%, #00E5FF 100%)',
            boxShadow: '0 6px 15px rgba(0, 229, 255, 0.5)',
          },
        },
        outlined: {
          borderColor: 'rgba(76, 185, 231, 0.5)',
          color: '#00E5FF',
          '&:hover': {
            borderColor: '#00E5FF',
            backgroundColor: 'rgba(0, 229, 255, 0.05)',
          },
        },
        text: {
          color: '#4CB9E7',
          '&:hover': {
            backgroundColor: 'rgba(76, 185, 231, 0.1)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#4CB9E7',
          '&:hover': {
            backgroundColor: 'rgba(76, 185, 231, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(76, 185, 231, 0.5)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(76, 185, 231, 0.8)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00E5FF',
              borderWidth: '2px',
              boxShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#00E5FF',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(76, 185, 231, 0.5)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(76, 185, 231, 0.8)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00E5FF',
            borderWidth: '2px',
            boxShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: 'rgba(76, 185, 231, 0.8)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 229, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 229, 255, 0.2)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(76, 185, 231, 0.1)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 229, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 229, 255, 0.2)',
            },
            '& .MuiListItemIcon-root': {
              color: '#00E5FF',
            },
            '& .MuiListItemText-primary': {
              color: '#00E5FF',
              fontWeight: 500,
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(76, 185, 231, 0.1)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          minWidth: '40px',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: 'rgba(255, 255, 255, 0.9)',
        },
        secondary: {
          color: 'rgba(255, 255, 255, 0.6)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(76, 185, 231, 0.3)',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
          margin: 8,
        },
        switchBase: {
          padding: 1,
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#00E5FF',
              opacity: 1,
              border: 'none',
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#00E5FF',
            border: '6px solid #fff',
          },
        },
        thumb: {
          width: 24,
          height: 24,
        },
        track: {
          borderRadius: 26 / 2,
          backgroundColor: 'rgba(255, 255, 255, 0.3)',
          opacity: 1,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(76, 185, 231, 0.2)',
          '&.MuiChip-colorPrimary': {
            backgroundColor: 'rgba(0, 229, 255, 0.2)',
            color: '#00E5FF',
            boxShadow: '0 0 5px rgba(0, 229, 255, 0.3)',
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: 'rgba(123, 104, 238, 0.2)',
            color: '#9F8FEF',
            boxShadow: '0 0 5px rgba(123, 104, 238, 0.3)',
          },
        },
        deleteIcon: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&:hover': {
            color: 'rgba(255, 255, 255, 0.9)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(10, 36, 99, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(76, 185, 231, 0.3)',
          boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)',
          fontSize: '0.75rem',
          padding: '8px 12px',
          borderRadius: 6,
        },
        arrow: {
          color: 'rgba(10, 36, 99, 0.95)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(10, 36, 99, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 12,
          boxShadow: '0 0 30px rgba(0, 229, 255, 0.3), inset 0 0 30px rgba(0, 229, 255, 0.05)',
          border: '1px solid rgba(76, 185, 231, 0.3)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#00E5FF',
          fontWeight: 600,
          textShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
          padding: '24px 24px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 36, 99, 0.6)',
          backdropFilter: 'blur(10px)',
          borderRadius: 12,
          boxShadow: '0 0 20px rgba(0, 229, 255, 0.2), inset 0 0 20px rgba(0, 229, 255, 0.05)',
          border: '1px solid rgba(76, 185, 231, 0.3)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 36, 99, 0.8)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(10, 36, 99, 0.4)',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 229, 255, 0.1) !important',
          },
        },
        head: {
          backgroundColor: 'rgba(10, 36, 99, 0.8) !important',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(76, 185, 231, 0.2)',
          padding: '16px',
        },
        head: {
          color: '#00E5FF',
          fontWeight: 600,
          backgroundColor: 'rgba(10, 36, 99, 0.8)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 3,
          backgroundColor: 'rgba(76, 185, 231, 0.2)',
        },
        bar: {
          borderRadius: 3,
          background: 'linear-gradient(90deg, #1E5F8C 0%, #00E5FF 100%)',
          boxShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: '#00E5FF',
          boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backdropFilter: 'blur(10px)',
        },
        standardSuccess: {
          backgroundColor: 'rgba(46, 204, 113, 0.2)',
          color: '#2ecc71',
          border: '1px solid rgba(46, 204, 113, 0.5)',
          '& .MuiAlert-icon': {
            color: '#2ecc71'
          }
        },
        standardError: {
          backgroundColor: 'rgba(231, 76, 60, 0.2)',
          color: '#e74c3c',
          border: '1px solid rgba(231, 76, 60, 0.5)',
          '& .MuiAlert-icon': {
            color: '#e74c3c'
          }
        },
        standardWarning: {
          backgroundColor: 'rgba(241, 196, 15, 0.2)',
          color: '#f1c40f',
          border: '1px solid rgba(241, 196, 15, 0.5)',
          '& .MuiAlert-icon': {
            color: '#f1c40f'
          }
        },
        standardInfo: {
          backgroundColor: 'rgba(52, 152, 219, 0.2)',
          color: '#3498db',
          border: '1px solid rgba(52, 152, 219, 0.5)',
          '& .MuiAlert-icon': {
            color: '#3498db'
          }
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(76, 185, 231, 0.3)',
        },
        indicator: {
          backgroundColor: '#00E5FF',
          height: 3,
          boxShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            color: '#00E5FF',
            textShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 229, 255, 0.1)',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 36, 99, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 12,
          boxShadow: '0 0 20px rgba(0, 229, 255, 0.2), inset 0 0 20px rgba(0, 229, 255, 0.05)',
          border: '1px solid rgba(76, 185, 231, 0.3)',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '16px 0',
            boxShadow: '0 0 25px rgba(0, 229, 255, 0.3), inset 0 0 25px rgba(0, 229, 255, 0.1)',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(76, 185, 231, 0.2)',
          '&.Mui-expanded': {
            borderBottom: '1px solid rgba(76, 185, 231, 0.3)',
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 229, 255, 0.2)',
              color: '#00E5FF',
              boxShadow: '0 0 5px rgba(0, 229, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(0, 229, 255, 0.3)',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(76, 185, 231, 0.1)',
            },
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#00E5FF',
          height: 8,
        },
        thumb: {
          height: 24,
          width: 24,
          backgroundColor: '#fff',
          boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
          '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: '0 0 15px rgba(0, 229, 255, 0.7)',
          },
          '&:before': {
            display: 'none',
          },
        },
        track: {
          background: 'linear-gradient(90deg, #1E5F8C 0%, #00E5FF 100%)',
          border: 'none',
          boxShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
        },
        rail: {
          opacity: 0.5,
          backgroundColor: 'rgba(76, 185, 231, 0.3)',
        },
        mark: {
          backgroundColor: 'rgba(76, 185, 231, 0.5)',
          height: 8,
          width: 1,
          marginTop: -3,
        },
        markActive: {
          opacity: 1,
          backgroundColor: 'currentColor',
        },
      },
    },
  },
});

export default theme;
